/**
 * View-transition wrapper for the module-pane open/close flip.
 *
 * The browser's View Transitions API does exactly what a hand-rolled flight
 * surface can't: it SNAPSHOTS the real pixels of the chat panel (messages,
 * composer, module card — anything with a view-transition-name in
 * ChatBox.css), applies the layout flip underneath, and morphs the old
 * image into the new live render. The user keeps seeing their actual chat
 * content the whole way — no hidden content, no empty traveling card, no
 * fade-from-blank at landing.
 *
 * `animate` is decided by the caller (user-intent write + desktop split +
 * no reduced-motion — the same gates the old flight used); hydration /
 * conversation-switch / auto-cleanup writes never come through here.
 * Unsupported browsers (no startViewTransition) get the plain instant flip.
 *
 * flushSync: startViewTransition captures "new" after its callback resolves,
 * so the React commit (including cascading useLayoutEffect updates like the
 * docked-exit's expandChatPane) must flush synchronously inside it.
 *
 * Rapid toggles are safe: starting a new transition while one is animating
 * makes the browser skip the old one to its end state.
 */
import { flushSync } from 'react-dom';

/**
 * `transitionClass` (optional) is added to <html> for the duration of the
 * transition and removed when it finishes, so CSS can give a specific flip its
 * own choreography — e.g. `vt-pane-closing` slides the module pane out to the
 * left instead of the default morph. Inert on the instant (non-animated) path.
 */
// Hard ceiling on how long a flip may hold the ::view-transition overlay. The
// longest healthy flip is the 0.5s vt-pane-closing morph (10-view-transitions.css),
// so 800ms clears it comfortably while bounding any stuck flip. See WATCHDOG below.
const VT_STUCK_DEADLINE_MS = 800;

// --- Mid-flip input rescue --------------------------------------------------
// The watchdog bounds a STUCK flip, but even a healthy flip holds the
// ::view-transition overlay for its full 180-500ms morph, and that overlay
// intercepts every pointer event (the `::view-transition{pointer-events:none}`
// opt-out is ineffective — see WATCHDOG note). Hit-testing during the morph
// resolves to <html>, so a rail/button press inside the window silently dies —
// the intermittent "clicking the sidebar does nothing". User input must beat
// decoration, in two parts (listeners registered once per document):
//   1. pointerdown while a flip is animating → skipTransition() immediately.
//      The overlay tears down, so the press's pointerup lands on live DOM.
//   2. The click synthesized from that press can still collapse to <html>
//      (a click's target is the common ancestor of its down/up targets), so a
//      click that lands on the bare document element right after a mid-flip
//      skip is re-dispatched to the interactive element under the pointer.
const RESCUE_WINDOW_MS = 1000;
let activeTransition: ViewTransition | null = null;
let rescueUntil = 0;
let rescueListenersInstalled = false;

function installRescueListeners(): void {
  if (rescueListenersInstalled) return;
  rescueListenersInstalled = true;
  document.addEventListener('pointerdown', () => {
    if (!activeTransition) return;
    try {
      activeTransition.skipTransition();
    } catch {
      // already finished/skipped — nothing to do
    }
    activeTransition = null;
    rescueUntil = performance.now() + RESCUE_WINDOW_MS;
  }, true);
  document.addEventListener('click', (e) => {
    if (!rescueUntil || performance.now() > rescueUntil) return;
    // One press, one rescue: the first click after a mid-flip skip consumes
    // the window whether or not it needs forwarding.
    rescueUntil = 0;
    if (e.target !== document.documentElement) return; // landed on live DOM
    const under = document.elementFromPoint(e.clientX, e.clientY);
    const interactive = under?.closest<HTMLElement>(
      'button, a[href], [role="button"], input, select, textarea, summary, [tabindex]',
    );
    // Forward ONLY inside the sidebar rail — fixed chrome that does not
    // reflow during the pane flip. The flip's real DOM commits BEFORE the
    // morph plays (flushSync in flipPaneWithViewTransition), so inside the
    // reflowing chat/module region a coordinate-based forward could activate
    // a control the user never saw (the split reassigns up to ~540px of
    // width). There, the press already cleared the overlay via the
    // pointerdown skip — the user's next click lands normally on the final
    // layout they can now see.
    if (!interactive || !interactive.closest('.sidebar-rail')) return;
    // Suppress the original <html>-targeted event so document-level bubble
    // listeners see exactly ONE logical click — the synthetic one below,
    // which re-dispatches from the real target through React and any
    // outside-click listeners like an ordinary bubbled click.
    e.stopPropagation();
    interactive.click();
  }, true);
}

export function flipPaneWithViewTransition(
  apply: () => void,
  animate: boolean,
  transitionClass?: string,
): void {
  // typeof guard doubles as the runtime feature check for browsers that
  // predate the API (lib.dom types it unconditionally).
  if (
    !animate
    || typeof document === 'undefined'
    || typeof document.startViewTransition !== 'function'
  ) {
    apply();
    return;
  }
  if (transitionClass) document.documentElement.classList.add(transitionClass);
  const cleanup = () => {
    if (transitionClass) document.documentElement.classList.remove(transitionClass);
  };
  const transition = document.startViewTransition(() => {
    flushSync(apply);
  });
  installRescueListeners();
  activeTransition = transition;

  // WATCHDOG (issue: "sidebar stuck until refresh"). While a view transition is
  // active, Chrome's ::view-transition overlay intercepts EVERY pointer event —
  // and `::view-transition { pointer-events: none }` does NOT opt it out (verified
  // ineffective in Chrome 149: the overlay still eats clicks for the whole flip).
  // Normally that window is ~180ms (open) / ~500ms (close). But if `finished`
  // never resolves — e.g. the tab is backgrounded mid-flip so the browser pauses
  // the animation, or the morph is otherwise interrupted — the overlay and the
  // <html> class persist and the entire app is click-dead until a page reload.
  // skipTransition() force-ends a stuck flip (overlay clears immediately); the
  // watchdog guarantees we can never be permanently frozen by one.
  const watchdog = setTimeout(() => {
    try {
      transition.skipTransition();
    } catch {
      // already finished/skipped — nothing to do
    }
    if (activeTransition === transition) activeTransition = null;
    cleanup();
  }, VT_STUCK_DEADLINE_MS);

  void transition.finished.finally(() => {
    if (activeTransition === transition) activeTransition = null;
    clearTimeout(watchdog);
    cleanup();
  });
}
